(() => {
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.emptyState = this.closest('#SearchDialog')?.querySelector('[data-search-state="empty"]');
      this.controller = null;
      this.activeIndex = -1;
      this.input?.addEventListener('input', () => this.onInput());
      this.input?.addEventListener('keydown', (event) => this.onKeydown(event));
    }
    async onInput() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.controller?.abort();
        this.controller = null;
        this.results.innerHTML = '';
        if (this.emptyState) this.emptyState.hidden = false;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.setAttribute('aria-activedescendant', '');
        this.activeIndex = -1;
        this.setStatus('');
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      if (this.emptyState) this.emptyState.hidden = true;
      this.setStatus('Loading suggestions.');
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('predictive search unavailable');
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const content = documentFragment.querySelector('[data-predictive-content]');
        this.results.innerHTML = content?.innerHTML || '';
        this.input.setAttribute('aria-expanded', 'true');
        this.activeIndex = -1;
        this.input.setAttribute('aria-activedescendant', '');
        this.setStatus(this.results.querySelector('[data-result-count]')?.textContent || 'Suggestions ready.');
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.results.innerHTML = '<div class="system-state" data-search-state="unavailable"><h3>Search is temporarily unavailable.</h3><p>Use the main navigation or try again.</p></div>';
        if (this.emptyState) this.emptyState.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.setAttribute('aria-activedescendant', '');
        this.setStatus('Search suggestions are temporarily unavailable.');
      }
    }
    onKeydown(event) {
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (!options.length) return;
      if (event.key === 'Escape') {
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        this.input.setAttribute('aria-activedescendant', '');
        this.activeIndex = -1;
        return;
      }
      if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        options[this.activeIndex].querySelector('a')?.click();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      this.activeIndex = event.key === 'ArrowDown'
        ? (this.activeIndex + 1) % options.length
        : (this.activeIndex - 1 + options.length) % options.length;
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
      const active = options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
